import { errors } from "@pax/contract-kit";
import { z } from "zod";

/**
 * The lowest account standing tier a route accepts; every tier above it passes too. A route that
 * accepts every tier, and signed-out visitors, names none.
 */
export const MinimumTier = z.enum(["suspicious", "normal"]).meta({
  id: "MinimumTier",
  description:
    "The lowest standing tier the route accepts: suspicious (writes and signup credits) or normal (the credit refresh, AI calls, payouts, and account changes). Higher tiers pass too.",
});
export type MinimumTier = z.infer<typeof MinimumTier>;

/**
 * Which rate-limit budget a route draws from. Signed-in callers draw from their own; signed-out
 * visitors only reach `read` and `search` routes, under their address's. `session` is the sign-in
 * check, which costs a Castle call and is always counted by address.
 */
export const RouteCost = z.enum(["read", "search", "write", "ai", "session"]).meta({
  id: "RouteCost",
  description:
    "The rate-limit budget the route draws from: read, search, write, or ai per signed-in user, or session per address for the sign-in check.",
});
export type RouteCost = z.infer<typeof RouteCost>;

/** The costs a public route may declare, since signed-out visitors only browse and search. */
export const PUBLIC_ROUTE_COSTS: readonly RouteCost[] = ["read", "search"];

/** Why an account was banned, in words support and the account's owner can act on. */
export const BanCategory = z.enum(["staff", "moderation"]).meta({
  id: "BanCategory",
  description:
    "staff: Pax staff suspended the account. moderation: staff banned the account from the whole site for what it posted.",
});
export type BanCategory = z.infer<typeof BanCategory>;

/** What a caller does next when its session falls short of a route's tier, or is still pending. */
export const StepUpStep = z.enum(["sign_in_check", "bot_check", "verify_person"]).meta({
  id: "StepUpStep",
  description:
    "sign_in_check: check the pending session, posting a fresh Castle request token, and on the web a reCAPTCHA token, to the sign-in check. bot_check: run reCAPTCHA on the web, or attest an App Attest key on iOS, and post the proof to the step-up route. verify_person: show the ways to verify the account; today that's adding a card through a card verification.",
});
export type StepUpStep = z.infer<typeof StepUpStep>;

/** The step-ups a client completes through `POST /api/risk/v1alpha1/step-ups`. */
export const OfferedStepUpStep = z.enum(["bot_check"]);
export type OfferedStepUpStep = z.infer<typeof OfferedStepUpStep>;

export const CastleRequestToken = z.string().min(1).max(8_192).meta({
  id: "CastleRequestToken",
  description:
    "A request token from Castle's browser or iOS SDK, minted just before the request. Minting one fingerprints the device, so clients mint one only to check a pending session or run a bot check. Each lasts 120 seconds.",
  example: "test|device:chrome_on_mac|ip:us-ca|policy.action:allow",
});

/** A way to verify a person that works today. Phone and ID are coming. */
export const VerifyPersonMethod = z.enum(["card"]).meta({
  id: "VerifyPersonMethod",
  description: "card: add a card through a card verification. Phone and ID aren't live yet.",
});
export type VerifyPersonMethod = z.infer<typeof VerifyPersonMethod>;

export const StepUpRequiredData = z
  .strictObject({
    step: StepUpStep,
    tier: MinimumTier,
    action: z.string().meta({
      description:
        "The refused route's summary, such as \"Claim free credits\", which clients show as what couldn't be completed.",
    }),
    methods: z.array(VerifyPersonMethod).optional().meta({
      description:
        "With `verify_person` only: the methods this account can still use. Empty once the account has used every live one.",
    }),
  })
  .meta({
    id: "StepUpRequiredData",
    description:
      "The step the caller completes before retrying, the tier the route needs, and what the route does, which clients use to say what couldn't be completed.",
    example: {
      step: "verify_person",
      tier: "normal",
      action: "Claim free credits",
      methods: ["card"],
    },
  });
export type StepUpRequiredData = z.infer<typeof StepUpRequiredData>;

export const AccountBannedData = z.strictObject({ category: BanCategory }).meta({
  id: "AccountBannedData",
  description: "Why the account was banned.",
  example: { category: "staff" },
});
export type AccountBannedData = z.infer<typeof AccountBannedData>;

const riskRefusals = {
  STEP_UP_REQUIRED: {
    status: 403,
    message: "Complete the step in data.step, then retry the request.",
    data: StepUpRequiredData,
  },
  ACCOUNT_BANNED: {
    status: 403,
    message: "This account is banned.",
    data: AccountBannedData,
  },
} as const;

/**
 * Declared once on every Product API procedure, because every one is rate limited by its cost
 * and checked against its lowest tier and the caller's ban.
 */
export const productApiErrors = {
  ...riskRefusals,
  ...errors.rateLimited,
} as const;
